/**
 * Step 2: Joint ↔ Servo ID Mapping
 * Configure servo IDs, direction, and offsets for each joint
 */

import { useCallback } from "react";
import {
  Box,
  Title,
  Text,
  Table,
  NumberInput,
  Select,
  Stack,
  Paper,
  Group,
  Badge,
  ScrollArea,
} from "@mantine/core";
import { useConfigureStore } from "../ConfigureStore";
import { useWebSocket } from "../../../WebSocket";

export function ServoMappingStep() {
  const {
    joints,
    servoMappings,
    updateServoMapping,
    highlightedJoint,
    setHighlightedJoint,
  } = useConfigureStore();

  // Use Builder's WebSocket for sending highlight messages
  const { sendHighlightJoint } = useWebSocket();

  // Handle joint highlighting - sends message to server for axis visualization
  const handleHighlightJoint = useCallback((jointName: string) => {
    setHighlightedJoint(jointName);
    sendHighlightJoint(jointName);
  }, [setHighlightedJoint, sendHighlightJoint]);

  // Clear highlighting when mouse leaves
  const handleClearHighlight = useCallback(() => {
    setHighlightedJoint(null);
    sendHighlightJoint(""); // Empty string clears the visualization
  }, [setHighlightedJoint, sendHighlightJoint]);

  // Filter to only actuated joints (non-fixed)
  const actuatedJoints = joints.filter((j) => j.type !== "fixed");

  if (actuatedJoints.length === 0) {
    return (
      <Box>
        <Title order={3} mb="xs">
          Servo Mapping
        </Title>
        <Text c="dimmed">
          No actuated joints found in the URDF. Only fixed joints were detected.
        </Text>
      </Box>
    );
  }

  return (
    <Stack gap="lg">
      <Box>
        <Title order={3} mb="xs">
          Joint ↔ Servo ID Mapping
        </Title>
        <Text c="dimmed" size="sm">
          Configure the servo ID, direction, and angle offset for each actuated joint.
        </Text>
      </Box>

      <Paper withBorder>
        <ScrollArea>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Joint Name</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Servo ID</Table.Th>
                <Table.Th>Direction</Table.Th>
                <Table.Th>Angle Offset (rad)</Table.Th>
                <Table.Th>Limits (rad)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {actuatedJoints.map((joint) => {
                const mapping = servoMappings.find(
                  (m) => m.jointName === joint.name
                );
                if (!mapping) return null;

                const isHighlighted = highlightedJoint === joint.name;

                return (
                  <Table.Tr
                    key={joint.name}
                    onClick={() => handleHighlightJoint(joint.name)}
                    onMouseEnter={() => handleHighlightJoint(joint.name)}
                    onMouseLeave={handleClearHighlight}
                    style={{
                      cursor: "pointer",
                      backgroundColor: isHighlighted
                        ? "var(--mantine-color-red-1)"
                        : undefined,
                      transition: "background-color 0.15s ease",
                    }}
                  >
                    <Table.Td>
                      <Text
                        size="sm"
                        fw={isHighlighted ? 700 : 500}
                        style={{ fontFamily: "monospace" }}
                        c={isHighlighted ? "red" : undefined}
                      >
                        {joint.name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light">
                        {joint.type}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        size="xs"
                        min={0}
                        max={255}
                        step={1}
                        placeholder="ID"
                        value={mapping.servoId ?? ""}
                        onChange={(value) =>
                          updateServoMapping(joint.name, {
                            servoId: typeof value === "number" ? value : null,
                          })
                        }
                        style={{ width: 80 }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Select
                        size="xs"
                        data={[
                          { value: "1", label: "+1 (Normal)" },
                          { value: "-1", label: "-1 (Reversed)" },
                        ]}
                        value={String(mapping.direction)}
                        onChange={(value) =>
                          updateServoMapping(joint.name, {
                            direction: value === "-1" ? -1 : 1,
                          })
                        }
                        style={{ width: 120 }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        size="xs"
                        step={0.01}
                        decimalScale={3}
                        placeholder="0.0"
                        value={mapping.angleOffset}
                        onChange={(value) =>
                          updateServoMapping(joint.name, {
                            angleOffset: typeof value === "number" ? value : 0,
                          })
                        }
                        style={{ width: 100 }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Text size="xs" c="dimmed">
                          [{joint.lowerLimit.toFixed(2)}, {joint.upperLimit.toFixed(2)}]
                        </Text>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      <Paper withBorder p="md" bg="gray.0">
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Configuration Notes
          </Text>
          <Text size="xs" c="dimmed">
            <strong>Servo ID:</strong> The hardware ID of the servo motor (0-255).
          </Text>
          <Text size="xs" c="dimmed">
            <strong>Direction:</strong> Set to -1 if the servo rotates in the opposite direction.
          </Text>
          <Text size="xs" c="dimmed">
            <strong>Angle Offset:</strong> Added to the commanded angle to correct for zero position.
          </Text>
        </Stack>
      </Paper>
    </Stack>
  );
}
